/** @format */

import React, {useCallback, useEffect, useState} from 'react';
import { TextField }                             from '@material-ui/core';
import I18n from '../../components/Translate/I18n';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { configDefault } from '../../core/config';
import { useDispatch } from 'react-redux';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import Card from '../../components/Card/Card';
import CardHeader from '../../components/Heading/CardHeaderExt';
import CardBody from '../../components/Card/CardBody';
import DashboardIcon from '@material-ui/icons/Dashboard';
import { useHistory, useParams } from 'react-router-dom';
import FractionListUsedList from './ServiceCodeEditComponents/FractionListUsedList';
import FractionListAvaibleList from './ServiceCodeEditComponents/FractionListAvaibleList';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import { serviceCodeConstants } from '../../modules/servicecode/reducers';
import ServiceCodeHistory from './ServiceCodeHistory';
import EditButton from '../../components/Buttons/EditButton';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import HistoryButton from '../../components/Buttons/HistoryButton';
import { history as HistoryCore } from '../../core/history';
import { ServiceCodeCollectionInterface } from '../../interfaces/collections/ServiceCodeCollectionInterface';
import ServiceCode from '../../interfaces/platform/ServiceCode';
import SelectFromRest from "../../components/Form/SelectFromRest";
import {PlatformResellerCollectionInterface} from "../../interfaces/collections/PlatformResellerCollectionInterface";
import {platformResellerConstants} from "../../modules/platformreseller/actions";

interface ServiceCodeFormInterface {
    text: string;
    description?: string | null;
    platformResellerId?: number | null;
}

interface LocationStateInterface {
    serviceCodeName: string;
    serviceCodeDescription: string;
    serviceCodePlatformResellerId: number;
    history: boolean;
}

export const ServiceCodeEdit = (): React.ReactElement => {
    const PLATFORM_RESELLER_URI = '/platform_resellers';

    const dispatch = useDispatch();

    const { id } = useParams<{ id: string }>();

    const { location } = useHistory<LocationStateInterface>();

    const { serviceCodeName, serviceCodeDescription, serviceCodePlatformResellerId, history } = location.state;

    const [editMode, setEditMode] = useState<boolean>(false);

    const [serviceCodeForm, setServiceCodeForm] = useState<ServiceCodeFormInterface>({
        text: serviceCodeName,
        description: serviceCodeDescription,
        platformResellerId: serviceCodePlatformResellerId
    });

    const [platformResellerList, setPlatformResellerList] = useState<PlatformResellerCollectionInterface | null>(null);

    const changePlatformResellerId = (e: any, stateName: string, require: boolean): void => {
        if (stateName === 'platformResellerId' ) {
            setServiceCodeForm({...serviceCodeForm, platformResellerId: e.target.value });
        }
    }

    const editModeHandle = (): void => {
        setEditMode(!editMode);
    };

    const formEditCallback = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const name = event.target.name;
        const value = event.target.value;
        setServiceCodeForm({
            ...serviceCodeForm,
            [name]: value,
        });
    };

    const saveServiceCodeEdit = (): void => {
        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.putWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath(`/service_codes/${id}`, configDefault.PLATFORM_API_PREFIX)),
            serviceCodeConstants.SERVICE_CODE_EDIT,
            serviceCodeForm,
            (data: ServiceCode) => {
                setEditMode(false);
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    };

    const cancelServiceCodeEdit = (): void => {
        setServiceCodeForm({
            text: serviceCodeName,
            description: serviceCodeDescription,
            platformResellerId: serviceCodePlatformResellerId,
        });
    };

    const showHistory = (param: { id: number; history: boolean }): void => {
        const redirectUri = FormGeneralHelper.getRouteUrl('/service-code-edit/' + param.id);

        HistoryCore.push(redirectUri, {
            history: param.history,
        });
    };

    const loadPlatformResellers = useCallback(
        (uri?: string | null): void => {
            if (!uri) {
                uri = FormGeneralHelper.getPath(PLATFORM_RESELLER_URI, configDefault.PLATFORM_API_PREFIX);
            }

            const loader = new ItemLoaders(dispatch);
            loader.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                platformResellerConstants.PLATFORM_RESELLER_LIST,
                [{ name: 'itemsPerPage', value: 9999 }],
                [],
                (data: PlatformResellerCollectionInterface) => {
                    setPlatformResellerList(data);
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [dispatch]
    );

    useEffect(() => {
        const itemLoaders = new ItemLoaders(dispatch);
        loadPlatformResellers();
        itemLoaders.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/service_codes', configDefault.PLATFORM_API_PREFIX)),
            serviceCodeConstants.SERVICE_CODE_LIST,
            [],
            [],
            (data: ServiceCodeCollectionInterface) => {
                dispatch({
                    type: serviceCodeConstants.SERVICE_CODE_LIST,
                    collections: data,
                });
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    }, [dispatch, loadPlatformResellers]);

    if(!platformResellerList) {
        return <div>Loading...</div>;
    }

    return history ? (
        <ServiceCodeHistory />
    ) : (
        <GridContainer spacing={2}>
            <GridItem xs={12}>
                <Card>
                    <CardHeader
                        icon={<DashboardIcon />}
                        trans="service_code:l.service_code_name"
                        additionalContent={
                            <>
                                <EditButton
                                    nonTableButton
                                    style={{ padding: '0 5px 5px 5px' }}
                                    callback={editModeHandle}
                                    tooltipTitle="service_code:tt.toggle_edit_mode"
                                    tooltipPlacement="bottom-start"
                                />
                                <HistoryButton
                                    nonTableButton
                                    style={{ padding: '0 5px 5px 5px' }}
                                    callback={showHistory}
                                    callbackParam={{ id: id, history: true }}
                                    tooltipTitle="service_code:tt.show_service_code_history"
                                    tooltipPlacement="bottom-start"
                                />
                            </>
                        }
                    />
                    <CardBody>
                        <GridContainer>
                            <GridItem xs={4}>
                                <GridContainer>
                                    <GridItem xs={12} style={{ margin: 10 }}>
                                        <TextField
                                            fullWidth
                                            label={<I18n trans={'service_code:l.service_code_name'} />}
                                            value={serviceCodeForm.text}
                                            name={'text'}
                                            onChange={editMode ? formEditCallback : (): void => {}}
                                        />
                                    </GridItem>
                                    <GridItem xs={12} style={{ margin: 10 }}>
                                        <TextField
                                            fullWidth
                                            label={<I18n trans={'service_code:l.service_code_description'} />}
                                            name={'description'}
                                            value={serviceCodeForm.description}
                                            onChange={editMode ? formEditCallback : (): void => {}}
                                        />
                                    </GridItem>
                                    <GridItem xs={12} style={{ margin: 10 }}>
                                        <SelectFromRest
                                            callback={editMode ? changePlatformResellerId : (): void => {}}
                                            required={true}
                                            addEmpty={false}
                                            restElements={platformResellerList ?? { 'hydra:member': [] }}
                                            stateName={'platformResellerId'}
                                            stateObjectState={'platformResellerId'}
                                            selectedValue={serviceCodeForm.platformResellerId}
                                            propKey="id"
                                            propValue="mainname"
                                            displayEmpty={true}
                                            emptyLabel={'services_activation:h.select_missing'}
                                            label={'services_activation:form.select_partner'}
                                        />
                                    </GridItem>
                                </GridContainer>
                                {(serviceCodeName !== serviceCodeForm.text || serviceCodeDescription !== serviceCodeForm.description || serviceCodePlatformResellerId !== serviceCodeForm.platformResellerId) && editMode && (
                                    <>
                                        <CustomTextButtom size={'sm'} label={'general:common.save'} callback={saveServiceCodeEdit} />
                                        <CustomTextButtom size={'sm'} label={'general:common.cancel'} callback={cancelServiceCodeEdit} />
                                    </>
                                )}
                            </GridItem>
                        </GridContainer>
                    </CardBody>
                </Card>
            </GridItem>
            <GridItem xs={12}>
                <Card>
                    <CardHeader icon={<SwapHorizIcon />} trans="service_code:h.service_code_fraction_edit" />
                    <CardBody>
                        <GridContainer>
                            <GridItem xs={12} md={6}>
                                <FractionListAvaibleList serviceCodeId={parseInt(id)} />
                            </GridItem>
                            <GridItem xs={12} md={6}>
                                <FractionListUsedList serviceCodeId={parseInt(id)} />
                            </GridItem>
                        </GridContainer>
                    </CardBody>
                </Card>
            </GridItem>
        </GridContainer>
    );
};
